import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { STORY_SCENES } from "./scrollStoryModel";
import { createProductStoryTimeline } from "./productStoryAnimation";
import {
  CustomizationSurface,
  DashboardSurface,
  MenuSurface,
  QrSurface,
  ReservationSurface,
} from "./ProductStoryVisuals";

gsap.registerPlugin(ScrollTrigger);

const VISUALS = {
  menu: MenuSurface,
  customize: CustomizationSurface,
  qr: QrSurface,
  reserve: ReservationSurface,
  dashboard: DashboardSurface,
};

// Positions are percentages of the pinned stage; depth scales the scroll
// parallax in productStoryAnimation.
const FOOD_EMOJIS = [
  { emoji: "🍊", top: "12%", left: "6%", size: "2.6rem", depth: 1.2 },
  { emoji: "🍕", top: "68%", left: "3%", size: "2.3rem", depth: 0.8 },
  { emoji: "🌶️", top: "22%", left: "47%", size: "1.8rem", depth: 1.5 },
  { emoji: "🍔", top: "84%", left: "64%", size: "2.1rem", depth: 1 },
  { emoji: "🥘", top: "7%", left: "31%", size: "2rem", depth: 0.6 },
  { emoji: "🍤", top: "44%", left: "1.5%", size: "1.8rem", depth: 0.9 },
  { emoji: "🍹", top: "80%", left: "90%", size: "2.1rem", depth: 1.3 },
  { emoji: "🧡", top: "10%", left: "91%", size: "1.6rem", depth: 0.7 },
];

const SCENE_EMOJIS = {
  menu: "🍊",
  customize: "🌶️",
  qr: "🍹",
  reserve: "🥘",
  dashboard: "🍕",
};

function SceneTitle({ title, highlight }) {
  const index = highlight ? title.indexOf(highlight) : -1;
  if (index < 0) return title;

  return (
    <>
      {title.slice(0, index)}
      <span className="text-[#dd6a12]">{highlight}</span>
      {title.slice(index + highlight.length)}
    </>
  );
}

function StoryVisual({ sceneKey }) {
  const Visual = VISUALS[sceneKey];
  return Visual ? <Visual /> : null;
}

export default function ProductStory() {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const copyRefs = useRef([]);
  const progressRefs = useRef([]);

  useLayoutEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;

    let disposed = false;
    const media = gsap.matchMedia();
    const context = gsap.context(() => {
      media.add(
        "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
        () => {
          const mode = window.innerWidth < 1024 ? "tablet" : "desktop";
          createProductStoryTimeline({
            gsap,
            root,
            stage: stageRef.current,
            copyItems: copyRefs.current,
            progressItems: progressRefs.current,
            mode,
            viewportHeight: window.innerHeight,
          });
        }
      );
    }, root);

    const refresh = () => {
      if (!disposed) ScrollTrigger.refresh();
    };

    window.addEventListener("load", refresh);
    document.fonts?.ready?.then(refresh);

    return () => {
      disposed = true;
      window.removeEventListener("load", refresh);
      media.revert();
      context.revert();
    };
  }, []);

  return (
    <section
      id="product-story"
      ref={sectionRef}
      dir="rtl"
      aria-labelledby="product-story-title"
      // Position is set inline, not via Tailwind's `relative`: the Tailwind
      // config uses `important: true`, so `.relative` would override the
      // inline `position: fixed` ScrollTrigger applies while pinning, and
      // the section would scroll away leaving an empty pin spacer.
      style={{ position: "relative" }}
      className="overflow-hidden bg-white text-[#102b23]"
    >
      <h2 id="product-story-title" className="sr-only">
        از منوی دیجیتال تا مدیریت کامل رستوران
      </h2>

      <div className="relative hidden min-h-screen md:block motion-reduce:hidden">
        <div className="pointer-events-none absolute inset-0 z-20" aria-hidden="true">
          {FOOD_EMOJIS.map(({ emoji, top, left, size, depth }) => (
            <span
              data-story-emoji
              data-depth={depth}
              key={emoji}
              className="absolute select-none leading-none"
              style={{ top, left, fontSize: size }}
            >
              {emoji}
            </span>
          ))}
        </div>

        <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-[minmax(17rem,.75fr)_minmax(28rem,1.25fr)] items-center gap-10 px-8 py-20 lg:gap-16 lg:px-12">
          <div className="relative z-20 min-h-[22rem]" aria-hidden="true">
            {STORY_SCENES.map((scene, index) => (
              // Centred with flex rather than a translate class: Tailwind's
              // !important transforms would override the timeline's y.
              <article
                key={scene.key}
                ref={(node) => {
                  copyRefs.current[index] = node;
                }}
                className="absolute inset-0 flex flex-col justify-center"
              >
                <div className="flex items-center gap-3 text-xs font-black text-[#c2540a]">
                  <span>{scene.step}</span>
                  <span className="h-px w-10 bg-primary/60" />
                  <span>{scene.eyebrow}</span>
                </div>
                <h3 className="mt-5 max-w-md text-3xl font-black leading-[1.45] lg:text-4xl">
                  <SceneTitle title={scene.title} highlight={scene.highlight} />
                </h3>
                <p className="mt-5 max-w-md text-sm leading-8 text-[#4a5a54] lg:text-base">
                  {scene.description}
                </p>
              </article>
            ))}
          </div>

          <div
            ref={stageRef}
            className="relative z-10 flex h-[min(42rem,76vh)] items-center justify-center will-change-transform"
            aria-hidden="true"
          >
            {/* Brand green panel the product surfaces sit on, contained
                inside the white section rather than a full-bleed wall. */}
            <div className="absolute inset-0 rounded-[2.5rem] bg-secondary" />
            <MenuSurface />
            <CustomizationSurface />
            <QrSurface />
            <ReservationSurface />
            <DashboardSurface />
          </div>
        </div>

        <div
          className="absolute inset-x-8 bottom-7 z-30 mx-auto flex max-w-7xl gap-2 lg:inset-x-12"
          aria-hidden="true"
        >
          {STORY_SCENES.map((scene, index) => (
            <span
              key={scene.key}
              className="h-0.5 flex-1 overflow-hidden rounded-full bg-[#102b23]/10"
            >
              <span
                ref={(node) => {
                  progressRefs.current[index] = node;
                }}
                className="block h-full w-full bg-primary"
              />
            </span>
          ))}
        </div>
      </div>

      <div className="relative space-y-16 px-5 py-20 md:hidden motion-reduce:block sm:px-8">
        {STORY_SCENES.map((scene) => (
          <article
            data-static-scene={scene.key}
            key={scene.key}
            className="mx-auto max-w-xl"
          >
            <div className="flex items-center gap-3 text-xs font-black text-[#c2540a]">
              <span>{scene.step}</span>
              <span className="h-px w-8 bg-primary/60" />
              <span>{scene.eyebrow}</span>
            </div>
            <h3 className="mt-4 text-2xl font-black leading-[1.5]">
              <SceneTitle title={scene.title} highlight={scene.highlight} />
            </h3>
            <p className="mt-3 text-sm leading-7 text-[#4a5a54]">
              {scene.description}
            </p>
            <div className="relative mt-8 flex h-[29rem] items-center justify-center overflow-hidden rounded-[2rem] bg-secondary">
              <span
                className="absolute left-4 top-4 text-2xl leading-none"
                aria-hidden="true"
              >
                {SCENE_EMOJIS[scene.key]}
              </span>
              <StoryVisual sceneKey={scene.key} />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
